import Tag from 'components/Tags';
import { Avatar, Button, Flex, Tooltip } from 'antd';
import {
  capitalizeWords,
  getGoogleMapURL,
  isMobileScreenSize,
  renderDate,
  renderInfoValue,
  validateImageURL,
} from 'utils/helpers';

import '../../style.scss';
import { getStatusTagColor } from '../../helper';

import NoImage from 'assets/no-image.png';
import { ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import StatusTag from 'components/StatusTag';

// Constants
const VERIFIED = 'VERIFIED';
const UNVERIFIED = 'UNVERIFIED';
const PENDING = 'PENDING';
const NONE = 'NONE';
const FARMER = 'Farmer';
const GROUP_LEADER = 'Group Leader';

export const getPhoneVerificationStatus = (status) => {
  const verificationStatus = status.toUpperCase();

  const phoneVerificationStatusMap = {
    VERIFIED_SENT: {
      status: VERIFIED,
      message: 'Farmer has read WA message',
      class: 'tag-phone-verified',
    },
    VERIFIED_READ: {
      status: VERIFIED,
      message: 'WA Message delivered to farmer',
      class: 'tag-phone-verified',
    },
    VERIFIED: {
      status: VERIFIED,
      message: 'WA Message delivered to farmer',
      class: 'tag-phone-verified',
    },
    PENDING: {
      status: PENDING,
      message: 'Phone verification pending',
      class: 'tag-phone-verification-pending ',
    },
    REQUEST_PENDING: {
      status: PENDING,
      message: 'Phone verification pending',
      class: 'tag-phone-verification-pending ',
    },
    UNVERIFIED: {
      status: UNVERIFIED,
      message: 'Phone verification failed',
      class: 'tag-phone-not-verified',
    },
    NONE: {
      status: NONE,
      message: 'No Verification',
      class: 'tag-phone-none',
    },
  };
  return phoneVerificationStatusMap[verificationStatus] || null;
};

export const getFarmerRole = (status) => {
  return status ? GROUP_LEADER : FARMER;
};

const tableDataStyle = {
  margin: 0,
  cursor: 'pointer',
};

const renderLocation = (location) => {
  const y = location?.split(',')?.[0];
  const x = location?.split(',')?.[1];

  if (
    !x ||
    !y ||
    x === 'undefined' ||
    y === 'undefined' ||
    x === 'null' ||
    y === 'null'
  )
    return <>N/A</>;

  return (
    <a
      className="link-text"
      href={getGoogleMapURL(x, y)}
      target="_blank"
      rel="noreferrer"
    >
      Go to Location
    </a>
  );
};

export const renderContractStatus = (status) => {
  const contractStatus = status ? status : 'NONE';
  const contractConfig = {
    NONE: {
      text: 'Contract Needed',
      type: 'red',
    },
    VERIFIED: {
      text: 'Has Contract',
      type: 'green',
    },
  };

  return (
    <StatusTag
      text={contractConfig?.[contractStatus]?.text}
      type={contractConfig?.[contractStatus]?.type}
    />
  );
};

const renderVerificationStatus = (status) => {
  const verificationStatus = status ? 'verified' : 'unverified';
  return <StatusTag status={verificationStatus} />;
};

const renderFarmerTableHeader = (handleClick) => {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      onCell: (data) => ({ onClick: () => handleClick(data) }),
      className: 'clickable',
      render: (name, rowData) => (
        <Flex align="center" justify="space-between" gap={4}>
          <Flex
            align="center"
            gap={8}
            className="d-flex align-items-center gap-2"
          >
            <Avatar
              src={
                validateImageURL(rowData?.profileImage)
                  ? rowData?.profileImage
                  : NoImage
              }
            />
            <div className="first-column-text"> {name}</div>
          </Flex>
          {!isMobileScreenSize ? (
            <Button className="table-click-indicator" type="text">
              <ArrowRightOutlined />
            </Button>
          ) : null}
        </Flex>
      ),
      fixed: 'left',
      width: isMobileScreenSize ? 160 : 250,
    },
    {
      title: 'Group Name',
      dataIndex: ['cube', 'name'],
      key: 'groupName',
      render: (groupName) => (
        <p style={tableDataStyle}>{renderInfoValue(groupName)}</p>
      ),
      width: 200,
    },
    {
      title: (
        <>
          Farmer Status
          <Tooltip title="Farmer participation in a Planting Group">
            <InfoCircleOutlined
              style={{ color: '#00000073', marginLeft: '8px' }}
            />
          </Tooltip>
        </>
      ),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          label={capitalizeWords(status)}
          color={getStatusTagColor(status)}
        />
      ),
      width: 180,
    },
    {
      title: 'Agronomist',
      dataIndex: ['cube', 'rizeusersByFieldCoordinator', 'name'],
      key: 'agronomistName',
      render: (agronomistName) => (
        <p style={tableDataStyle}>{renderInfoValue(agronomistName)}</p>
      ),
      width: 200,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber) => (
        <p style={tableDataStyle}>
          {phoneNumber ? (
            renderInfoValue(phoneNumber)
          ) : (
            <span className="no-phone">No Phone</span>
          )}
        </p>
      ),
      width: 200,
    },
    {
      title: 'OTP Verification',
      dataIndex: 'otpVerified',
      key: 'phoneVerification',
      render: renderVerificationStatus,
      width: 180,
    },
    {
      title: 'Verification Channel',
      dataIndex: 'otpChannel',
      key: 'waVerifiedStatus',
      render: (channel, data) =>
        data.otpVerified ? (channel === 'WA' ? 'Whatsapp' : channel) : 'N/A',
      width: 200,
    },
    {
      title: 'Contract Status',
      dataIndex: 'contractStatus',
      key: 'contractStatus',
      render: renderContractStatus,
      width: 180,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: renderLocation,
      width: 150,
    },
    {
      title: 'Owned Hectares',
      key: 'area',
      dataIndex: 'area',
      render: (area) => <p style={tableDataStyle}>{renderInfoValue(area)}</p>,
      width: 180,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => (
        <p style={tableDataStyle}>{renderDate(createdAt)}</p>
      ),
      width: 180,
    },
  ];
};

export default renderFarmerTableHeader;
